/**
 * PermissionsGate — fullscreen "tap to start AR" overlay.
 *
 * iOS Safari demands that camera, device-orientation, and device-motion
 * permission requests all happen inside the **same** synchronous user
 * gesture. This DOM-based overlay handles that by exposing a single tap
 * button; in the click handler we kick off all the permission requests
 * at once and wait for them to settle.
 *
 * Not a Phaser scene because it needs to live before the Phaser canvas
 * exists (so the camera permission can complete before the canvas tries
 * to render transparent).
 *
 * Usage from `bootGame()`:
 *
 *   const gate = new PermissionsGate({
 *     needs: ['camera', 'orientation', 'motion'],
 *     onGranted: async () => { ... start camera, install sensors ... }
 *   });
 *   gate.show();
 */
export class PermissionsGate {
  /**
   * @param {object} opts
   * @param {Array<'camera'|'microphone'|'orientation'|'motion'>} opts.needs
   * @param {() => Promise<void>|void} opts.onGranted
   * @param {(failed: string[]) => void} [opts.onDenied]
   * @param {string} [opts.title='Tap to enable AR']
   * @param {string} [opts.subtitle]
   * @param {string} [opts.buttonLabel='Start']
   * @param {object} [opts.theme]
   */
  constructor(opts) {
    this.needs = opts.needs || [];
    this.onGranted = opts.onGranted || (() => {});
    this.onDenied = opts.onDenied || (() => {});
    this.title = opts.title || 'Tap to enable AR';
    this.subtitle = opts.subtitle || 'We need access to your camera and motion sensors.';
    this.buttonLabel = opts.buttonLabel || 'Start';
    this.theme = Object.assign({
      bg: '#12131a',
      text: '#f3f4f9',
      textDim: '#9aa0b8',
      primary: '#6d8aff'
    }, opts.theme || {});

    this._root = null;
    this._busy = false;
  }

  show() {
    if (this._root || typeof document === 'undefined') return;

    const root = document.createElement('div');
    Object.assign(root.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '99999',
      background: this.theme.bg,
      color: this.theme.text,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      padding: '32px',
      textAlign: 'center'
    });

    const title = document.createElement('h1');
    title.textContent = this.title;
    title.style.margin = '0 0 12px';
    title.style.fontSize = '32px';

    const subtitle = document.createElement('p');
    subtitle.textContent = this.subtitle;
    subtitle.style.margin = '0 0 32px';
    subtitle.style.color = this.theme.textDim;
    subtitle.style.maxWidth = '420px';

    const button = document.createElement('button');
    button.textContent = this.buttonLabel;
    Object.assign(button.style, {
      background: this.theme.primary,
      color: '#fff',
      border: 'none',
      borderRadius: '12px',
      padding: '16px 36px',
      fontSize: '18px',
      fontWeight: '700',
      cursor: 'pointer',
      minWidth: '200px',
      minHeight: '54px'
    });

    const status = document.createElement('p');
    status.style.margin = '24px 0 0';
    status.style.color = this.theme.textDim;
    status.style.minHeight = '20px';

    button.addEventListener('click', async () => {
      if (this._busy) return;
      this._busy = true;
      button.disabled = true;
      status.textContent = 'Requesting permissions…';

      const failed = [];
      try {
        await this._requestAll(failed);
      } catch (err) {
        failed.push(String(err && err.message || err));
      }

      if (failed.length === 0) {
        try {
          await this.onGranted();
        } catch (err) {
          status.textContent = 'Failed to start: ' + (err && err.message || err);
          this._busy = false;
          button.disabled = false;
          return;
        }
        this.hide();
      } else {
        status.textContent = 'Denied: ' + failed.join(', ');
        button.textContent = 'Try again';
        this._busy = false;
        button.disabled = false;
        this.onDenied(failed);
      }
    });

    root.appendChild(title);
    root.appendChild(subtitle);
    root.appendChild(button);
    root.appendChild(status);
    document.body.appendChild(root);
    this._root = root;
  }

  hide() {
    if (!this._root) return;
    this._root.style.transition = 'opacity 200ms ease';
    this._root.style.opacity = '0';
    setTimeout(() => {
      if (this._root && this._root.parentNode) {
        this._root.parentNode.removeChild(this._root);
      }
      this._root = null;
    }, 200);
  }

  async _requestAll(failed) {
    // Camera + microphone are bundled because both go through getUserMedia.
    if (this.needs.includes('camera') || this.needs.includes('microphone')) {
      const constraints = {
        video: this.needs.includes('camera') ? { facingMode: 'environment' } : false,
        audio: this.needs.includes('microphone')
      };
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        // We only needed the prompt — release the stream now. The real
        // CameraPassthrough will request it again, which will reuse the
        // already-granted permission and not show another prompt.
        for (const track of stream.getTracks()) track.stop();
      } catch (err) {
        if (this.needs.includes('camera')) failed.push('camera');
        if (this.needs.includes('microphone')) failed.push('microphone');
      }
    }

    if (this.needs.includes('orientation') && typeof window !== 'undefined') {
      const DOE = window.DeviceOrientationEvent;
      if (DOE && typeof DOE.requestPermission === 'function') {
        try {
          const result = await DOE.requestPermission();
          if (result !== 'granted') failed.push('orientation');
        } catch (_) {
          failed.push('orientation');
        }
      }
    }

    if (this.needs.includes('motion') && typeof window !== 'undefined') {
      const DME = window.DeviceMotionEvent;
      if (DME && typeof DME.requestPermission === 'function') {
        try {
          const result = await DME.requestPermission();
          if (result !== 'granted') failed.push('motion');
        } catch (_) {
          failed.push('motion');
        }
      }
    }
  }
}
